import { useEffect, useState } from 'react'
import { VIDEO_PROGRESS_MESSAGES } from '../types'

const ROTATION_MS = 10_000

/**
 * Rotates through a list of progress messages every 10s while active.
 * Generic over the message set so posts / carousels / videos can share the
 * same hook with their own copy.
 *
 * Depends on `messages.length` (a primitive) rather than the array identity,
 * so callers passing inline arrays don't reset the rotation every render.
 * Length is the only thing the modulo needs; the render path always reads
 * the latest `messages` reference for the actual string lookup.
 */
export function useGenerationProgress(
  messages: readonly string[],
  isActive: boolean
): string | null {
  const [index, setIndex] = useState(0)

  useEffect(() => {
    if (!isActive) return
    setIndex(0)
    const interval = setInterval(() => {
      setIndex(i => (i + 1) % messages.length)
    }, ROTATION_MS)
    return () => clearInterval(interval)
  }, [isActive, messages.length])

  return isActive ? messages[index % messages.length] : null
}

/**
 * Backwards-compatible wrapper for video-status callers that already imported
 * `useVideoProgress`. New callers should use `useGenerationProgress` directly.
 */
export function useVideoProgress(isActive: boolean): string | null {
  return useGenerationProgress(VIDEO_PROGRESS_MESSAGES, isActive)
}
